import React, { useEffect, useState } from "react";
import "./DoctorProfileComplete.scss";
import { useNavigate } from "react-router-dom";
import API from "../../../Api/Api";
import ENDPOINTS from "../../../Api/Endpoints";
import { getSocket } from "../../../Api/socket";
import { tokenManager } from "../../../Api/tokenManager";

import BackG from "../../../Assets/doctorprofilebg.png";
import tickR from "../../../Assets/tick.png";

export default function DoctorProfileComplete() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const isHospitalFlow = (() => {
    try {
      return String(window?.location?.pathname || "").toLowerCase().startsWith("/hospital");
    } catch (_) {
      return false;
    }
  })();
  const meEndpoint = isHospitalFlow ? ENDPOINTS.HOSPITAL_ME : ENDPOINTS.DOCTOR_ME;
  const dashboardPath = isHospitalFlow ? "/hospital-dashboard" : "/doctor-dashboard";
  const socketRole = isHospitalFlow ? "hospital" : "doctor";
  /** Unified patient / doctor / hospital entry (not standalone doctor-login). */
  const authGatewayPath = isHospitalFlow ? "/get-started?provider=hospital" : "/get-started?provider=doctor";
  const providerRole = isHospitalFlow ? "hospital" : "doctor";

  const extractProfileStatus = (response) => {
    const payload = response?.data;
    const candidates = [
      payload?.status,
      payload?.adminApprovalStatus,
      payload?.data?.status,
      payload?.data?.adminApprovalStatus,
      payload?.doctor?.status,
      payload?.doctor?.adminApprovalStatus,
      payload?.hospital?.status,
      payload?.hospital?.adminApprovalStatus,
      payload?.data?.doctor?.status,
      payload?.data?.doctor?.adminApprovalStatus,
      payload?.data?.hospital?.status,
      payload?.data?.hospital?.adminApprovalStatus,
    ];

    for (const value of candidates) {
      const normalized = String(value ?? "").trim().toLowerCase();
      if (normalized) return normalized;
    }
    return "";
  };

  useEffect(() => {
    let mounted = true;

    const tryRefreshSession = async () => {
      const refresh = tokenManager.getRefreshTokenForRole(providerRole);
      if (!refresh) return false;
      const url = isHospitalFlow
        ? ENDPOINTS.HOSPITAL_REFRESH_TOKEN
        : ENDPOINTS.DOCTOR_REFRESH_TOKEN;
      try {
        const { data } = await API.post(url, { refreshToken: refresh });
        const d = data?.data || data;
        const accessToken = d?.accessToken || d?.token;
        const newRefresh = d?.refreshToken;
        if (accessToken) {
          tokenManager.setTokensForRole(providerRole, accessToken, newRefresh);
          return true;
        }
      } catch (_) {}
      return false;
    };

    const goToAuthGateway = () => {
      if (!mounted) return;
      navigate(authGatewayPath, { replace: true });
    };

    const checkStatus = async () => {
      const apply = (res) => {
        const status = extractProfileStatus(res);
        if (!mounted) return;
        setChecking(false);
        if (status === "approved") {
          navigate(dashboardPath, { replace: true });
        }
      };

      try {
        const res = await API.get(meEndpoint);
        apply(res);
      } catch (err) {
        const code = err?.response?.status;
        if (code === 401 || code === 403) {
          const refreshed = await tryRefreshSession();
          if (refreshed) {
            try {
              const res2 = await API.get(meEndpoint);
              apply(res2);
              return;
            } catch {
              if (!mounted) return;
              setChecking(false);
              goToAuthGateway();
              return;
            }
          }
          if (!mounted) return;
          setChecking(false);
          goToAuthGateway();
          return;
        }
        if (!mounted) return;
        setChecking(false);
      }
    };

    checkStatus();

    let pollId = null;
    const startPolling = () => {
      if (pollId) return;
      pollId = window.setInterval(checkStatus, 8000);
    };
    const stopPolling = () => {
      if (!pollId) return;
      window.clearInterval(pollId);
      pollId = null;
    };

    const socket = getSocket({ role: socketRole });
    const onApproved = () => {
      checkStatus();
    };
    if (!socket) {
      startPolling();
    } else {
      socket.on("doctor:status", onApproved);
      socket.on("hospital:status", onApproved);
      socket.on("connect", stopPolling);
      socket.on("disconnect", startPolling);
      socket.on("connect_error", startPolling);
      if (!socket.connected) startPolling();
    }

    return () => {
      mounted = false;
      stopPolling();
      if (socket) {
        try {
          socket.off("doctor:status", onApproved);
          socket.off("hospital:status", onApproved);
          socket.off("connect", stopPolling);
          socket.off("disconnect", startPolling);
          socket.off("connect_error", startPolling);
        } catch (_) {}
      }
    };
  }, [authGatewayPath, dashboardPath, isHospitalFlow, meEndpoint, navigate, providerRole, socketRole]);

  return (
    <section
      className="doctorComplete"
      style={{ backgroundImage: `url(${BackG})` }}
    >
      <div className="doctorComplete__content">
        <figure className="rite-tick">
          <img src={tickR} alt="" />
        </figure>
        <h1 className="doctorComplete__title">
          Your Profile Has Been Successfully Completed
        </h1>

        <p className="doctorComplete__sub">
          Thank you for completing your professional profile. Your details have
          been securely submitted to HealBid and are now under review.
        </p>

        <p className="doctorComplete__note">
          *Our verification team will review your profile and documents to
          ensure authenticity and quality for patients.
        </p>

        {checking ? (
          <p className="doctorComplete__note">Checking approval status…</p>
        ) : null}
      </div>
    </section>
  );
}
